import { type FC, type FormEvent, useState } from "react";
import {
  AppBar,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  marketingErrorKey,
  useCreateContactListMutation,
  useUpdateContactListMutation,
} from "@/partner/entities/marketing";
import type { MutationErrorLike } from "@/partner/shared/lib";

const FORM_ID = "contact-list-name-form";

interface ContactListNameDialogProps {
  /** null: новый список; иначе переименование. */
  list: { id: string; name: string } | null;
  onClose: () => void;
  /** Список создан: страница переключает адрес на ?tab=edit&id=. */
  onCreated?: (id: string) => void;
}

/**
 * Название списка контактов: создание (?tab=create) и переименование из
 * диалога списка. Короткий диалог с шапкой AppBar, как PollLibraryDialog.
 */
export const ContactListNameDialog: FC<ContactListNameDialogProps> = ({
  list,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState(list?.name ?? "");
  const onError = (error: MutationErrorLike) =>
    enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" });
  const [createList, { loading: creating }] = useCreateContactListMutation({
    onCompleted: (data) => {
      enqueueSnackbar(t("marketingContacts.create.success"), {
        variant: "success",
      });
      if (onCreated) onCreated(data.createContactList.id);
      else onClose();
    },
    onError,
  });
  const [updateList, { loading: updating }] = useUpdateContactListMutation({
    onCompleted: () => {
      enqueueSnackbar(t("marketingContacts.edit.renamed"), {
        variant: "success",
      });
      onClose();
    },
    onError,
  });
  const loading = creating || updating;
  const trimmed = name.trim();

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!trimmed || loading) return;
    if (list) {
      void updateList({ variables: { id: list.id, name: trimmed } });
    } else {
      void createList({ variables: { name: trimmed } });
    }
  };

  return (
    <Dialog
      open
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <AppBar color="default" position="sticky" elevation={0}>
        <Toolbar>
          <IconButton edge="start" onClick={onClose} sx={{ mr: 1 }}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
            {list
              ? t("marketingContacts.edit.rename")
              : t("marketingContacts.create.title")}
          </Typography>
          <Button
            variant="contained"
            disableElevation
            type="submit"
            form={FORM_ID}
            disabled={loading || !trimmed}
          >
            {t("common.actions.save")}
          </Button>
        </Toolbar>
        <Divider />
      </AppBar>
      <DialogContent sx={{ p: 2 }}>
        <form id={FORM_ID} onSubmit={onSubmit}>
          <TextField
            label={t("marketingContacts.form.name")}
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
            autoFocus
            disabled={loading}
            sx={{ mt: 1 }}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
};
